import { equals } from 'ramda'
import React, { useCallback, memo } from 'react'
import { FixedSizeList } from 'react-window'
import { Box } from '@mui/material'
import { styled } from '@mui/material/styles'
import { VirtualizedListBaseProps } from './virtualized-list.types'
import { virtualizedListDefaultProps } from './constant'
import { nothing } from '$func/utils'

const ListContainer = styled(Box)`
  background-color: #fff;

  & > div {
    direction: inherit !important;
    text-align: inherit !important;
  }
`

const DEFAULT_LIST_PADDING = 5
const LOADING_ITEMS_COUNT = 3

export const VirtualizedListBase = React.forwardRef(
  function VirtualizedListBase<Item>(
    {
      height,
      items = [],
      itemCount,
      ListItem,
      SkeletonItem,
      EmptyStateComponent,
      itemSize,
      loading,
      renderItem,
      onItemsRendered,
      overscanCount,
      onScroll,
      className,
      listPadding = DEFAULT_LIST_PADDING,
    }: VirtualizedListBaseProps<Item>,
    ref: React.Ref<any>,
  ) {
    const renderRow = useCallback(
      memo(
        ({
          data,
          index,
          style,
        }: {
          data: Item[]
          index: number
          style: any
        }) => {
          const itemData = data[index]
          const modifiedStyle = { ...style, top: style.top + listPadding }

          if (index < items.length && renderItem)
            return renderItem({ itemData, index, style: modifiedStyle })

          return (
            <div key={index} style={modifiedStyle}>
              {index >= items.length
                ? SkeletonItem && <SkeletonItem />
                : ListItem && <ListItem {...itemData} />}
            </div>
          )
        },
        equals,
      ),
      [items.length, renderItem, ListItem, SkeletonItem, loading],
    )

    return (
      <ListContainer sx={{ height }}>
        {items.length === 0 && !loading ? (
          <EmptyStateComponent />
        ) : (
          <FixedSizeList
            className={className}
            onScroll={onScroll}
            ref={ref}
            onItemsRendered={onItemsRendered}
            itemData={items}
            height={height}
            width="100%"
            itemSize={itemSize}
            overscanCount={overscanCount}
            itemCount={itemCount + (loading ? LOADING_ITEMS_COUNT : 0)}
          >
            {renderRow}
          </FixedSizeList>
        )}
      </ListContainer>
    )
  },
)

VirtualizedListBase.defaultProps = {
  onItemsRendered: nothing,
  ...virtualizedListDefaultProps,
}
